import React, {
  FC,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  AlertProps,
  Box,
  Snackbar,
  SnackbarCloseReason,
  Typography,
} from '@mui/material';
import { ReactComponent as FacebookAdsIcon } from 'src/icons/facebook.svg';
import { ReactComponent as GoogleAdsIcon } from 'src/icons/googleAds.svg';
import { ReactComponent as GoogleAnalyticsIcon } from 'src/icons/googleAnalytics.svg';
import { ReactComponent as GoogleSearchConsoleIcon } from 'src/icons/googleSearchConsole.svg';
import { ReactComponent as ShopifyIcon } from 'src/icons/shopify.svg';
import { SnackbarContext } from 'src/providers/snackbar/context';
import {
  CloseButton,
  ErrorIcon,
  IconContainer,
  InfoIcon,
  SuccessIcon,
  Wrapper,
} from 'src/providers/snackbar/styled';
import { ConnectorType } from 'src/utils/gql/types';

interface AlertState {
  message: string;
  severity: AlertProps['severity'];
  type?: ConnectorType;
}

const ConnectorIcon: FC<{ type?: ConnectorType }> = ({ type }) => {
  switch (type) {
    case ConnectorType.FACEBOOK:
      return <FacebookAdsIcon width="40px" height="40px" />;
    case ConnectorType.GOOGLE_ADS:
      return <GoogleAdsIcon width="40px" height="40px" />;
    case ConnectorType.GOOGlE_ANALYTICS4:
      return <GoogleAnalyticsIcon width="40px" height="40px" />;
    case ConnectorType.GOOGLE_SEARCH_CONSOLE:
      return <GoogleSearchConsoleIcon width="40px" height="40px" />;
    case ConnectorType.SHOPIFY:
      return <ShopifyIcon width="40px" height="40px" />;
    default:
      return null;
  }
};

export const SnackbarProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState<AlertState>({
    message: '',
    severity: 'success',
  });

  // Debounce/dedupe notifiche per evitare spam e toast non chiudibili
  const [lastMessageTs, setLastMessageTs] = useState<number>(0);
  const DEDUPE_WINDOW_MS = 5000; // 5 secondi

  const showMessage = useCallback(
    (
      message: string,
      severity: AlertProps['severity'] = 'error',
      type?: ConnectorType,
    ) => {
      // Ignora errori rumorosi che non richiedono UX (es. oauth google unauthorized_client)
      if (
        typeof message === 'string' &&
        message.toLowerCase().includes('unauthorized_client')
      ) {
        return;
      }

      // Evita duplicati ravvicinati
      const now = Date.now();
      if (
        open &&
        message === alert.message &&
        now - lastMessageTs < DEDUPE_WINDOW_MS
      ) {
        return;
      }

      setAlert({ message, severity, type });
      setOpen(true);
      setLastMessageTs(now);
    },
    [alert.message, lastMessageTs, open],
  );

  const handleClose = (
    _: Event | SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  const handleCloseButton = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('🔴 CloseButton clicked - closing notification');
    setOpen(false);
  };

  return (
    <>
      <SnackbarContext.Provider value={showMessage}>
        {children}
      </SnackbarContext.Provider>
      <Snackbar
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
      >
        <Wrapper>
          <CloseButton
            onClick={handleCloseButton}
            role="button"
            aria-label="Chiudi notifica"
            title="Chiudi notifica"
          >
            <CloseIcon fontSize="small" />
          </CloseButton>
          <Box display="flex" gap="15px">
            <IconContainer>
              <ConnectorIcon type={alert.type} />
              {alert.severity === 'success' && !alert.type && <SuccessIcon />}
              {alert.severity === 'error' && <ErrorIcon />}
              {alert.severity === 'info' && <InfoIcon />}
            </IconContainer>
            <Box>
              <Typography textTransform="capitalize" fontWeight="600">
                {alert.severity}
              </Typography>
              <Typography variant="body2">{alert.message}</Typography>
            </Box>
          </Box>
        </Wrapper>
      </Snackbar>
    </>
  );
};
